import type { ContinentId } from './territories.js';

export const OCEANS = ['atlantic', 'mediterranean', 'indian', 'pacific', 'arctic-southern'] as const;
export type OceanId = (typeof OCEANS)[number];

export const OCEAN_NAMES: Record<OceanId, string> = {
  atlantic: 'Atlântico',
  mediterranean: 'Mediterrâneo',
  indian: 'Índico',
  pacific: 'Pacífico',
  'arctic-southern': 'Ártico e Austral',
};

export interface SeaZoneDef {
  id: string;
  name: string;
  ocean: OceanId;
}

function z(id: string, name: string, ocean: OceanId): SeaZoneDef {
  return { id, name, ocean };
}

/** Territórios marítimos (TERRITORIOS.md). */
export const SEA_ZONES: readonly SeaZoneDef[] = [
  // Atlântico (14)
  z('atlantico-norte-ocidental', 'Atlântico Norte Ocidental', 'atlantic'),
  z('atlantico-norte-central', 'Atlântico Norte Central', 'atlantic'),
  z('atlantico-norte-oriental', 'Atlântico Norte Oriental', 'atlantic'),
  z('mar-do-norte', 'Mar do Norte', 'atlantic'),
  z('mar-baltico', 'Mar Báltico', 'atlantic'),
  z('mar-da-noruega', 'Mar da Noruega', 'atlantic'),
  z('golfo-do-mexico', 'Golfo do México', 'atlantic'),
  z('mar-do-caribe', 'Mar do Caribe', 'atlantic'),
  z('atlantico-central', 'Atlântico Central', 'atlantic'),
  z('mar-golfo-da-guine', 'Golfo da Guiné', 'atlantic'),
  z('atlantico-sul-ocidental', 'Atlântico Sul Ocidental', 'atlantic'),
  z('atlantico-sul-central', 'Atlântico Sul Central', 'atlantic'),
  z('atlantico-sul-oriental', 'Atlântico Sul Oriental', 'atlantic'),
  z('mar-argentino', 'Mar Argentino', 'atlantic'),
  // Mediterrâneo (4)
  z('mediterraneo-ocidental', 'Mediterrâneo Ocidental', 'mediterranean'),
  z('mediterraneo-central', 'Mediterrâneo Central', 'mediterranean'),
  z('mediterraneo-oriental', 'Mediterrâneo Oriental', 'mediterranean'),
  z('mar-negro', 'Mar Negro', 'mediterranean'),
  // Índico (8)
  z('mar-vermelho', 'Mar Vermelho', 'indian'),
  z('mar-arabico', 'Mar Arábico', 'indian'),
  z('golfo-persico', 'Golfo Pérsico', 'indian'),
  z('baia-de-bengala', 'Baía de Bengala', 'indian'),
  z('indico-ocidental', 'Índico Ocidental', 'indian'),
  z('indico-central', 'Índico Central', 'indian'),
  z('indico-oriental', 'Índico Oriental', 'indian'),
  z('indico-sul', 'Índico Sul', 'indian'),
  // Pacífico (15)
  z('mar-da-china-meridional', 'Mar da China Meridional', 'pacific'),
  z('mar-da-china-oriental', 'Mar da China Oriental', 'pacific'),
  z('mar-do-japao', 'Mar do Japão', 'pacific'),
  z('mar-de-okhotsk', 'Mar de Okhotsk', 'pacific'),
  z('mar-de-bering', 'Mar de Bering', 'pacific'),
  z('pacifico-norte-ocidental', 'Pacífico Norte Ocidental', 'pacific'),
  z('pacifico-norte-central', 'Pacífico Norte Central', 'pacific'),
  z('pacifico-norte-oriental', 'Pacífico Norte Oriental', 'pacific'),
  z('pacifico-leste-tropical', 'Pacífico Leste Tropical', 'pacific'),
  z('pacifico-sul-oriental', 'Pacífico Sul Oriental', 'pacific'),
  z('pacifico-sul-central', 'Pacífico Sul Central', 'pacific'),
  z('pacifico-sul-ocidental', 'Pacífico Sul Ocidental', 'pacific'),
  z('mar-de-coral', 'Mar de Coral', 'pacific'),
  z('mares-do-arquipelago-malaio', 'Mares do Arquipélago Malaio', 'pacific'),
  z('mar-das-filipinas', 'Mar das Filipinas', 'pacific'),
  // Ártico e Austral (5)
  z('artico-ocidental', 'Ártico Ocidental', 'arctic-southern'),
  z('artico-oriental', 'Ártico Oriental', 'arctic-southern'),
  z('mar-da-groenlandia', 'Mar da Groenlândia', 'arctic-southern'),
  z('baia-de-hudson', 'Baía de Hudson', 'arctic-southern'),
  z('oceano-austral', 'Oceano Austral', 'arctic-southern'),
];

export const SEA_ZONE_BY_ID: Readonly<Record<string, SeaZoneDef>> = Object.fromEntries(
  SEA_ZONES.map((def) => [def.id, def]),
);

export const SEA_ZONE_IDS: readonly string[] = SEA_ZONES.map((def) => def.id);

export function isSeaZoneId(id: string): boolean {
  return id in SEA_ZONE_BY_ID;
}

export interface CanalDef {
  id: string;
  name: string;
  /** Território cujo dono controla a passagem. */
  territory: string;
  zones: readonly [string, string];
}

/** Canais: passagem só para o dono do território e quem ele autorizar (aliados ou tratados). */
export const CANALS: readonly CanalDef[] = [
  { id: 'suez', name: 'Canal de Suez', territory: 'egito', zones: ['mediterraneo-oriental', 'mar-vermelho'] },
  {
    id: 'panama',
    name: 'Canal do Panamá',
    territory: 'america-central',
    zones: ['mar-do-caribe', 'pacifico-leste-tropical'],
  },
];

/** Regiões para satélite estacionário: um continente ou um oceano. */
export type SatelliteRegion = { kind: 'continent'; id: ContinentId } | { kind: 'ocean'; id: OceanId };
